<template>
  <div
    v-auto-animate="{ duration: ANIMATION_DURATION_MS }"
    :style="{ '--ob-aa-animation-duration': `${ANIMATION_DURATION_MS}ms` }"
    class="aa-Accordion"
  >
    <div
      :class="{
        'aa-Header-static': disabled,
        'aa-Header-open': open,
        'aa-Header-borderless': borderless
      }"
      class="aa-Header"
      data-auto-testid="accordion-header"
      data-testid="accordion-header"
      @click.stop="onHeaderClick"
    >
      <div
        class="aa-HeaderMain"
        data-auto-testid="accordion-header-main"
        data-testid="accordion-header-main"
      >
        <AppIcon
          v-if="!disabled"
          :class="{ 'aa-Chevron-rotated': open }"
          class="aa-Chevron"
          data-auto-testid="accordion-chevron"
          data-testid="accordion-chevron"
          height="24"
          icon-name="okr-table-expand"
          width="24"
        />
        <span
          v-if="prependIcon || $slots['prepend-icon']"
          class="aa-PrependIcon"
          data-auto-testid="accordion-prepend-icon"
          data-testid="accordion-prepend-icon"
        >
          <slot name="prepend-icon">
            <AppIcon :icon-name="prependIcon" height="24" width="24" />
          </slot>
        </span>
        <div class="aa-Title">
          <slot name="title" />
        </div>
      </div>
      <span
        v-if="$slots['header-right']"
        class="aa-HeaderRight"
        data-auto-testid="accordion-header-right"
        data-testid="accordion-header-right"
        @click.stop
      >
        <slot name="header-right" />
      </span>
    </div>

    <div
      v-if="open"
      class="aa-Content"
      data-auto-testid="accordion-content"
      data-testid="accordion-content"
    >
      <slot name="content" />
    </div>
  </div>
</template>

<script setup>
import { vAutoAnimate } from '@formkit/auto-animate'

import { isAnyTextSelected } from '@/utils/text-selection'

import AppIcon from '@/components/ui/AppIcon/AppIcon'

const ANIMATION_DURATION_MS = 250

defineOptions({
  name: 'AppAccordion'
})

const open = defineModel('open', {
  type: Boolean
})

const props = defineProps({
  disabled: {
    type: Boolean
  },

  borderless: {
    type: Boolean
  },

  prependIcon: {
    type: String,
    default: null
  }
})

const onHeaderClick = () => {
  if (props.disabled || isAnyTextSelected()) return

  open.value = !open.value
}
</script>

<style lang="scss" scoped>
.aa-Accordion {
  display: flex;
  width: 100%;
  flex-direction: column;
  border-radius: $border-radius-md-next;
  border: 1px solid $grey-2-next;
  overflow: hidden;
}

.aa-Header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  width: 100%;
  padding: 16px 15px;
  background: $grey-3;
  color: $dark-1;
  font-size: $fs-16;
  font-weight: fw('medium');
  line-height: 24px;
  cursor: pointer;
  box-shadow: inset 0 -1px 0 0 transparent;
  transition: box-shadow var(--ob-aa-animation-duration) ease-in-out;

  &-static {
    cursor: default;
  }

  &-open {
    box-shadow: inset 0 -1px 0 0 $grey-2-next;
  }

  &-borderless {
    box-shadow: inset 0 -1px 0 0 transparent;
  }
}

.aa-HeaderMain {
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
  gap: 8px;
}

.aa-Header-static .aa-HeaderMain {
  cursor: default;
}

.aa-PrependIcon {
  display: flex;
  flex-shrink: 0;
  align-items: center;
}

.aa-Title {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.aa-HeaderRight {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.aa-Chevron {
  flex-shrink: 0;
  color: var(--ob-aa-chevron-color, #{$dark-2});
  transition: transform $transition-fast ease;

  &-rotated {
    transform: rotate(90deg);
  }
}

.aa-PrependIcon {
  color: var(--ob-aa-prepend-icon-color, inherit);
  margin-right: 8px;
}
</style>
